import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CreatePassword.scss";
import Input from "../../Components/Common/Input/Input";
import Button from "../../Components/Common/Button/Button";
import { post } from "../../utils/api/apiCaller";
import { ENDPOINTS } from "../../utils/api/endpoints";
import { validateEmail } from "./utils/loginValidation";
import RoleSelectModal from "../../Components/Common/RoleSelectModal/RoleSelectModal";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError("");
    const err = validateEmail(email.trim());
    if (err) {
      setEmailError(err);
      return;
    }
    setShowRoleModal(true);
  };

  const sendResetLink = async (role: string) => {
    setLoading(true);
    const { error } = await post(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      email: email.trim(),
      role,
    });
    setLoading(false);
    setShowRoleModal(false);
    if (error) {
      setApiError(error.message ?? "Something went wrong. Please try again.");
      return;
    }
    setSuccess(true);
  };

  if (success) {
    return (
      <div className="createPasswordScreen">
        <div className="cpStatusBox">
          <h2>Check Your Email</h2>
          <p className="cpStatusText">
            If an account exists with this email, a password reset link has been
            sent. Please check your email to continue resetting your password.
          </p>
          <Button
            className="createBtn cpStatusBtn"
            onClick={() => navigate("/", { replace: true })}
          >
            OK
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="createPasswordScreen">
      <div className="container">
        <div className="page">
          <section className="hero">
            <div className="hero-copy">
              <h1>Forgot Your Password?</h1>
              <p>
                Enter your registered email address and we'll send you a secure
                link to reset your password.
              </p>
            </div>
          </section>

          <section className="createPanel">
            <h2>Reset Password</h2>
            <p>We'll email you a secure link to reset your password.</p>
            <form onSubmit={handleSubmit} noValidate>
              <div className="field">
                <label htmlFor="fp-email">Email address</label>
                <Input
                  id="fp-email"
                  type="email"
                  placeholder="email@company.com"
                  value={email}
                  disabled={loading}
                  className={emailError ? "inputError" : ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                    if (emailError)
                      setEmailError(validateEmail(e.target.value.trim()));
                  }}
                />
                {emailError && (
                  <span className="fieldError" role="alert">
                    {emailError}
                  </span>
                )}
              </div>

              {apiError && (
                <p className="formError" role="alert">
                  {apiError}
                </p>
              )}

              <Button type="submit" className="createBtn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="btnSpinner" aria-hidden="true" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </form>
            <div className="hint">
              Remember your password? <a href="/">Sign In</a>
            </div>
          </section>
        </div>
      </div>

      <RoleSelectModal
        show={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        onConfirm={sendResetLink}
        loading={loading}
        confirmLabel="Send Reset Link"
      />
    </div>
  );
}
